import React, { useState } from "react";
import { videoPack } from "@/components/utils/videoPack";
import WhitelistButton from "@/components/atomics/whitelist-button";
import MintButton from "@/components/atomics/mint-button";
import ButtonSkeleton from "@/components/atomics/button-skeleton";
import {
  CollectionStats,
  CollectionMediaLoadingState,
} from "@/types/collection";
import { useTonPrice } from "@/hooks/useTonPrice";
import ConnectWalletModal from "@/components/atomics/connect-wallet-modal";
import MotionModal from "@/components/atomics/motion-modal";
import { useTonConnectUI } from "@tonconnect/ui-react";
import { requestProfilePrice } from "@/hooks/useCollectionContract";
import WhitelistSuccessMessage from "@/components/atomics/whitelist-success-message";

interface WhitelistStatusReturn {
  isWhitelisted: boolean | null;
  userProfile: any;
  isLoadingProfile: boolean;
  showWalletConnectionModal: boolean;
  showWhitelistSuccessMessage: boolean;
  setShowWhitelistSuccessMessage: (show: boolean) => void;
  requestProfile: () => Promise<any>;
  setShowWalletConnectionModal: (show: boolean) => void;
}

interface MintSectionProps {
  isOwnerView: boolean;
  loading: CollectionMediaLoadingState;
  stats: CollectionStats;
  walletAddress: string | null;
  collection: string;
  mintNft: (profileAddress: string | null) => void;
  isMinting: boolean;
  isMintingAllowed: boolean;
  whitelistStatus: WhitelistStatusReturn;
}

const MintSection = ({
  isOwnerView,
  loading,
  stats,
  walletAddress,
  collection,
  mintNft,
  isMinting,
  isMintingAllowed,
  whitelistStatus,
}: MintSectionProps) => {
  const [isIOS, setIsIOS] = useState<boolean>(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState<boolean>(false);
  const tonPrice = useTonPrice();
  const [tonConnectUI] = useTonConnectUI();

  const {
    isWhitelisted,
    userProfile,
    isLoadingProfile,
    showWalletConnectionModal,
    showWhitelistSuccessMessage,
    setShowWhitelistSuccessMessage,
    requestProfile,
    setShowWalletConnectionModal,
  } = whitelistStatus;

  const handleConnectWallet = async () => {
    try {
      await tonConnectUI.openModal();
    } catch (error) {
      console.error("Failed to open wallet connection modal:", error);
    }
  };

  const handleModalClose = () => {
    setShowWalletConnectionModal(false);
  };

  const renderButton = () => {
    if (stats.enable_whitelist && walletAddress && isWhitelisted === null) {
      return (
        <div className="w-[60%] opacity-0 animate-fade-in">
          <ButtonSkeleton className="w-full" />
        </div>
      );
    }

    if (stats.enable_whitelist && walletAddress && !isWhitelisted) {
      return (
        <div className="w-[60%] opacity-0 animate-fade-in">
          <WhitelistButton
            className="w-full"
            tonPrice={tonPrice ?? 0}
            price={Number(requestProfilePrice)}
            onClick={requestProfile}
            isLoading={isLoadingProfile}
            isApplied={userProfile?.is_deployed ?? false}
          />
        </div>
      );
    }

    return (
      <div className="w-[60%] opacity-0 animate-fade-in">
        <MintButton
          className="w-full"
          isSbt={stats.is_sbt}
          tonPrice={tonPrice || 0}
          price={stats.price}
          onClick={() =>
            mintNft(userProfile ? userProfile.profile_address : null)
          }
          isLoading={isMinting}
          isDisabled={!isMintingAllowed}
        />
      </div>
    );
  };

  return (
    <>
      <section className="px-[30px]">
        {!isOwnerView ? (
          <div className="w-full h-[310px] pt-[183px] flex justify-between items-center opacity-100 transition-all duration-500 ease-in-out">
            <div className="relative w-[36%] aspect-square flex items-center justify-center">
              {loading.nftImage ? (
                <div className="w-full h-full flex flex-col line-height-25 items-center justify-center text-[22px] bg-[#525252]">
                  <span>LOADING</span>
                  <span>MEDIA</span>
                </div>
              ) : (!stats.nft_image_type ||
                  stats.nft_image_type === "video/mp4") &&
                stats.nft_image_content ? (
                videoPack(isIOS, stats.nft_image_content, setIsVideoLoaded)
              ) : (
                <img src={stats.nft_image} className="object-contain" />
              )}
            </div>
            {renderButton()}
          </div>
        ) : (
          <div className="h-[310px] flex flex-col items-center justify-center pt-[183px] opacity-100 transition-all duration-500 ease-in-out">
            <span className="text-[30px] text-[#0099FE]">EARNED</span>
            <span className="text-[40px]">{stats.earned}</span>
            <span className="text-[30px] text-[#0099FE]">TON</span>
          </div>
        )}
      </section>
      <MotionModal isOpen={showWhitelistSuccessMessage}>
        <WhitelistSuccessMessage
          onClose={() => setShowWhitelistSuccessMessage(false)}
          onClick={() => setShowWhitelistSuccessMessage(false)}
        />
      </MotionModal>

      <MotionModal isOpen={showWalletConnectionModal}>
        <ConnectWalletModal
          onClose={handleModalClose}
          onClick={handleModalClose}
          onConnect={handleConnectWallet}
        />
      </MotionModal>
    </>
  );
};

export default MintSection;
